import { Controller } from '@hotwired/stimulus'

/**
 * GridStack Controller for customizable dashboard layouts
 *
 * Manages two pools of chart configurations:
 * 1. Grid (visible): Charts actively displayed and submitted with the form
 * 2. Pool (hidden): Charts wrapped in <fieldset disabled> to prevent form submission
 *
 * Deletion Strategy:
 * - On form save, the controller destroys ALL existing chart_configurations
 * - Then recreates them from the submitted form data (only visible charts)
 * - This is simpler than managing _destroy state in JavaScript
 * - Charts can be removed/re-added before save as an undo mechanism
 */
export default class extends Controller {
    static targets = ['grid', 'chartPool', 'availableChartsList', 'addChartDropdown']
    static outlets = ['show-element']
    static values = {
        columns: { type: Number, default: 24 },
        cellHeight: { type: Number, default: 240 },
        minWidth: { type: Number, default: 6 },
        minHeight: { type: Number, default: 1 },
        defaultWidth: { type: Number, default: 8 },
        defaultHeight: { type: Number, default: 2 },
    }

    grid = null

    connect() {
        // Load GridStack CSS asynchronously
        import('gridstack/dist/gridstack.min.css')
        import('./gridstack/gridstack-bootstrap.scss')
        this.element.classList.remove('d-none')

        // Initialize grid after a short delay to ensure DOM is ready
        requestAnimationFrame(async () => {
            await this.initializeGrid()
            this.attachEventListeners()
            this.updateAvailableChartsList()
        })
    }

    disconnect() {
        if (this.grid) {
            this.grid.destroy(false)
            this.grid = null
        }
    }

    async initializeGrid() {
        const gridOptions = {
            column: this.columnsValue, // Configurable column grid system
            cellHeight: this.cellHeightValue, // Configurable cell height in pixels
            acceptWidgets: false, // No external widgets
            disableOneColumnMode: false, // Enable mobile responsive
            float: false, // Prevent floating, maintain vertical order
            minRow: 1, // Minimum number of rows
            animate: true, // Smooth animations
            removable: false, // Don't allow removing items
            draggable: {
                handle: '.gridstack-drag-handle', // Only drag from specific handle
            },
            resizable: {
                handles: 'e, se, s, sw, w', // Enable all resize handles
            },
        }

        const { GridStack } = await import('gridstack')
        this.grid = GridStack.init(gridOptions, this.gridTarget)

        // Configure each grid item's constraints
        this.grid.getGridItems().forEach((el) => {
            const node = el.gridstackNode
            if (node) {
                // Set constraints
                this.grid.update(el, {
                    minW: this.minWidthValue, // Minimum 1/4 width
                    maxW: this.columnsValue, // Maximum full width
                    minH: this.minHeightValue, // Minimum height
                })
            }
        })
    }

    attachEventListeners() {
        if (!this.grid) return

        // Listen for any changes (drag, resize, etc.)
        this.grid.on('change', () => {
            this.saveGridState()
        })
    }

    saveGridState() {
        if (!this.grid) return

        const items = this.grid.getGridItems()

        items.forEach((el) => {
            const node = el.gridstackNode
            if (!node) return

            this.#updateFormField(el, 'grid_x', node.x ?? 0)
            this.#updateFormField(el, 'grid_y', node.y ?? 0)
            this.#updateFormField(el, 'grid_width', node.w ?? this.defaultWidthValue)
            this.#updateFormField(el, 'grid_height', node.h ?? this.defaultHeightValue)
            this.#updateFormField(el, 'grid_visible', true)
        })

        this.showElementOutlets.forEach((showElement) => showElement.show())
    }

    // Action to add chart from pool to grid
    addChart(event) {
        event.preventDefault()
        if (!this.grid || !this.hasChartPoolTarget) return

        const configurationId = event.currentTarget.dataset.chartConfigurationId
        const poolItem = this.chartPoolTarget.querySelector(
            `[data-chart-configuration-id="${configurationId}"]`,
        )

        if (!poolItem) {
            console.error(`Chart configuration ${configurationId} not found in pool`)
            return
        }

        // Move from pool to grid
        this.gridTarget.appendChild(poolItem)
        this.grid.makeWidget(poolItem)

        // Set default size for newly added chart
        this.grid.update(poolItem, {
            w: this.defaultWidthValue, // Default width from model
            h: this.defaultHeightValue, // Default height from model
            minW: this.minWidthValue,
            maxW: this.columnsValue,
            minH: this.minHeightValue,
        })

        this.#updateFormField(poolItem, 'grid_visible', true)

        this.updateAvailableChartsList()

        // Save state
        this.saveGridState()
    }

    // Action to remove chart from grid to pool
    removeChart(event) {
        event.preventDefault()
        event.stopPropagation() // Prevent drag from triggering
        if (!this.grid || !this.hasChartPoolTarget) return

        const element = event.currentTarget.closest('.grid-stack-item')

        if (!element) {
            console.error('Could not find chart element to remove')
            return
        }

        // Remove from grid (without destroying the element)
        this.grid.removeWidget(element, false)

        // Move to pool (inside disabled fieldset so it won't be submitted)
        const fieldset = this.chartPoolTarget.querySelector('fieldset')
        if (fieldset) {
            fieldset.appendChild(element)
        } else {
            this.chartPoolTarget.appendChild(element)
        }

        this.#updateFormField(element, 'grid_visible', false)

        // Update the available charts list
        this.updateAvailableChartsList()

        // Save state
        this.saveGridState()
    }

    // Update the available charts dropdown list
    // FIXME: This duplicates logic used in filter+drilldown dropdowns ... make generic dropdown active state manager
    updateAvailableChartsList() {
        if (!this.hasAvailableChartsListTarget || !this.hasChartPoolTarget) return

        const poolCharts = Array.from(
            this.chartPoolTarget.querySelectorAll('[data-chart-configuration-id]'),
        )
        const poolChartIds = poolCharts.map((el) => el.getAttribute('data-chart-configuration-id'))

        const dropdownItems = this.availableChartsListTarget.querySelectorAll(
            '[data-chart-configuration-id]',
        )
        dropdownItems.forEach((item) => {
            const configurationId = item.dataset.chartConfigurationId
            const parentLi = item.closest('li')
            if (parentLi) {
                if (poolChartIds.includes(configurationId)) {
                    parentLi.style.display = ''
                } else {
                    parentLi.style.display = 'none'
                }
            }
        })

        // Hide dropdown if no charts available
        if (this.hasAddChartDropdownTarget) {
            const hasAvailableCharts = poolChartIds.length > 0
            this.addChartDropdownTarget.classList.toggle('d-none', !hasAvailableCharts)
        }
    }

    #updateFormField(element, fieldName, value) {
        const field = element.querySelector(`[data-gridstack-field="${fieldName}"]`)
        if (field) {
            field.value = value
        }
    }
}
